import { graphql } from 'react-relay';

import type { Option } from '@atlaskit/editor-common/extensions';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type {
	labelResolverQuery$data,
	labelResolverQuery$variables,
} from './__generated__/labelResolverQuery.graphql.ts';

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- file name contains hyphens, incompatible with GraphQL naming
const labelResolverQuery = graphql`
	query labelResolverQuery($searchTerm: String!) {
		labelSearch(searchText: $searchTerm, limit: 25) {
			otherLabels {
				label
			}
			suggestedLabels {
				label
			}
		}
	}
`;
const isNonNullable = <T>(value: T): value is NonNullable<T> => value != null;

const extractResults = (
	labelResults: ReadonlyArray<
		| {
				readonly label: string | null | undefined;
		  }
		| null
		| undefined
	>,
) =>
	labelResults.filter(isNonNullable).map((res) => ({
		label: res.label ?? '',
		value: res.label ?? '',
	}));

const search = async (searchTerm?: string): Promise<Option[]> => {
	const result = await queryInterop<labelResolverQuery$data, labelResolverQuery$variables>({
		query: labelResolverQuery,
		variables: {
			searchTerm: searchTerm ?? '',
		},
	});

	const labelSearch = result?.data?.labelSearch;

	return [
		...extractResults(labelSearch?.suggestedLabels ?? []),
		...extractResults(labelSearch?.otherLabels ?? []),
	];
};

export const labelResolver = async (searchTerm?: string, selectedValue?: string | string[]) => {
	const searchers: Array<Promise<Option[]>> = [];

	if (searchTerm) {
		searchers.push(search(searchTerm));
	}

	if (selectedValue) {
		if (Array.isArray(selectedValue)) {
			searchers.push(
				Promise.resolve(
					selectedValue.map((label) => ({
						label,
						value: label,
					})),
				),
			);
		} else {
			searchers.push(
				Promise.resolve([
					{
						label: selectedValue,
						value: selectedValue,
					},
				]),
			);
		}
	}

	return (await Promise.all(searchers)).flat();
};
